<template>
  <v-row class="patient-information-container">
    <v-col v-if="orderInfoPreview" cols="12">
      <v-row class="patient-information">
        <v-col cols="12">
          <h3 class="text-primary weight-600">Medical Information</h3>
        </v-col>

        <v-col cols="12">
          <FeedbackInputWrapper
            :field-id="'medication'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.medication"
            :error-messages="
              props.isEditable && !fieldNotEmpty('medication') && props.formButtonClicked
                ? 'Please enter medication.'
                : null
            "
            label="medication">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <div v-if="orderInfoPreview.medication?.length > 0">
                <v-text-field
                  v-model="orderInfoPreview.medication"
                  type="text"
                  placeholder="Medication"
                  label="Medication*"
                  class="lh--force-white-background"
                  :readonly="!props.isEditable"
                  :disabled="!props.isEditable"
                  :error-messages="errorMessages"
                  @input="onInput"
                  @change="
                    (e) => {
                      onChange(e);
                      emit('updateOrderInfoField', 'medication', orderInfoPreview.medication);
                    }
                  "
                  @focus="onFocus"
                  @blur="onBlur" />
              </div>
              <div v-else>
                <MedicationSearchAutoComplete
                  :disabled="!props.isEditable"
                  :data="medicationSearchBoxTerms"
                  :required="true"
                  :allow-free-text-input="true"
                  :menu-props="{
                    contentClass: 'medication-autocomplete-menu',
                    maxHeight: 300,
                    position: 'absolute',
                    transition: false,
                    closeOnClick: true,
                    closeOnContentClick: true,
                    eager: true,
                  }"
                  @item-selected="
                    (event) => {
                      selectedMedication = event;
                      onInput(event);
                      emit('updateOrderInfoField', 'medication', orderInfoPreview.medication);
                    }
                  "
                  @focus="onFocus"
                  @blur="onBlur" />
              </div>
            </template>
          </FeedbackInputWrapper>
        </v-col>
        <v-col cols="6">
          <FeedbackInputWrapper
            :field-id="'frequency'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.frequency"
            label="frequency">
            <template #default="{ onInput, onChange, onFocus, onBlur }">
              <v-text-field
                v-model="orderInfoPreview.frequency"
                type="text"
                placeholder="Dosing Schedule [SIG]"
                label="Dosing Schedule [SIG]"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'frequency', orderInfoPreview.frequency);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="3">
          <FeedbackInputWrapper
            :field-id="'dailyDosage'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.dailyDosage"
            label="dailyDosage">
            <template #default="{ onInput, onChange, onFocus, onBlur }">
              <v-text-field
                v-model="orderInfoPreview.dailyDosage"
                type="text"
                placeholder="Dosage"
                label="Dosage"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'dailyDosage', orderInfoPreview.dailyDosage);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col v-if="isCoverMyMeds" cols="3">
          <FeedbackInputWrapper
            :field-id="'nationalDrugCode'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.nationalDrugCode"
            :error-messages="
              props.isEditable && !nationalDrugCodeValid('nationalDrugCode') && props.formButtonClicked
                ? 'Please enter 11 digit NDC.'
                : null
            "
            label="nationalDrugCode">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.nationalDrugCode"
                type="text"
                placeholder="National Drug Code (NDC)"
                label="National Drug Code (NDC)*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'nationalDrugCode', orderInfoPreview.nationalDrugCode);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>
        <v-col v-else cols="3">
          <FeedbackInputWrapper
            :field-id="'nationalDrugCode'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.nationalDrugCode"
            label="nationalDrugCode">
            <template #default="{ onInput, onChange, onFocus, onBlur }">
              <v-text-field
                v-model="orderInfoPreview.nationalDrugCode"
                type="text"
                placeholder="National Drug Code (NDC)"
                label="National Drug Code (NDC)"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'nationalDrugCode', orderInfoPreview.nationalDrugCode);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col v-if="isCoverMyMeds" cols="4">
          <FeedbackInputWrapper
            :field-id="'packageSizeUnit'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.packageSizeUnit"
            :error-messages="
              props.isEditable && !packageSizeUnitValid('packageSizeUnit') && props.formButtonClicked
                ? 'Please select a valid package suze unit.'
                : null
            "
            label="packageSizeUnit">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-select
                v-model="orderInfoPreview.packageSizeUnit"
                :items="requirementsStore.clientConfig?.intake?.packageSizeUnits || []"
                placeholder="Package Size Unit"
                label="Package Size Unit*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'packageSizeUnit', orderInfoPreview.packageSizeUnit);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="4">
          <FeedbackInputWrapper
            :field-id="'strength'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.strength"
            label="strength">
            <template #default="{ onInput, onChange, onFocus, onBlur }">
              <v-text-field
                v-model="orderInfoPreview.strength"
                type="text"
                placeholder="Strength"
                label="Strength"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'strength', orderInfoPreview.strength);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="4">
          <FeedbackInputWrapper
            :field-id="'routeAdministration'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.routeAdministration"
            label="Route of Administration">
            <template #default="{ onInput, onChange, onFocus, onBlur }">
              <v-text-field
                v-model="orderInfoPreview.routeAdministration"
                type="text"
                placeholder="Route of Administration"
                label="Route of Administration"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'routeAdministration', orderInfoPreview.routeAdministration);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="4">
          <FeedbackInputWrapper
            :field-id="'quantityPer30Days'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.quantityPer30Days"
            :error-messages="
              props.isEditable && !numericalFieldValid('quantityPer30Days') && props.formButtonClicked
                ? 'Please enter a numerical value for quantity.'
                : null
            "
            label="quantityPer30Days">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.quantityPer30Days"
                type="text"
                placeholder="Quantity"
                label="Quantity*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'quantityPer30Days', orderInfoPreview.quantityPer30Days);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="4">
          <FeedbackInputWrapper
            :field-id="'expectedLengthOfTherapy'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.expectedLengthOfTherapy"
            :error-messages="
              props.isEditable && !numericalFieldValid('expectedLengthOfTherapy') && props.formButtonClicked
                ? 'Please enter length of therapy in days.'
                : null
            "
            label="expectedLengthOfTherapy">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.expectedLengthOfTherapy"
                type="text"
                placeholder="Duration of Therapy"
                label="Duration of Therapy*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'expectedLengthOfTherapy', orderInfoPreview.expectedLengthOfTherapy);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>
        <v-col cols="3">
          <FeedbackInputWrapper
            :field-id="'treatmentStartDate'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.treatmentStartDate"
            label="treatmentStartDate">
            <template #default="{ onInput, onChange, onFocus, onBlur }">
              <v-text-field
                v-model="orderInfoPreview.treatmentStartDate"
                type="date"
                placeholder="Treatment Start Date"
                label="Treatment Start Date"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'treatmentStartDate', orderInfoPreview.treatmentStartDate);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>
        <v-col cols="1" class="d-flex align-center justify-center">
          <v-btn
            v-if="props.isEditable"
            icon="mdi-delete"
            variant="text"
            color="error"
            @click="emit('removePrimaryMedication')" />
        </v-col>

        <v-col v-if="isCoverMyMeds && newMedicationSelected" cols="12" class="mt-4">
          <v-btn v-if="props.isEditable" variant="outlined" @click="emit('changeFormulary')">Change Formulary</v-btn>
        </v-col>

        <v-col
          v-if="
            (isFax || isManual) && orderInfoPreview.extraMedications && orderInfoPreview.extraMedications.length > 0
          "
          cols="12">
          <div
            v-for="(extraMed, index) in orderInfoPreview.extraMedications"
            :key="index"
            class="extra-medication-block">
            <v-divider class="mt-4 mb-4" />
            <v-row>
              <v-col cols="12">
                <div v-if="extraMed && 'medication' in extraMed">
                  <FeedbackInputWrapper
                    :field-id="'extraMedication' + index"
                    :order-key="orderInfoPreview.orderKey"
                    :model-value="extraMed.medication"
                    :error-messages="
                      props.isEditable && !extraMedFieldNotEmpty(extraMed, 'medication') && props.formButtonClicked
                        ? 'Please enter medication.'
                        : null
                    "
                    :label="'extraMedication' + index">
                    <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
                      <div v-if="extraMed.medication?.length > 0">
                        <v-text-field
                          v-model="extraMed.medication"
                          type="text"
                          placeholder="Medication"
                          label="Medication*"
                          class="lh--force-white-background"
                          :readonly="!props.isEditable"
                          :disabled="!props.isEditable"
                          :error-messages="errorMessages"
                          @input="onInput"
                          @change="
                            (e) => {
                              onChange(e);
                              emit('updateExtraMedicationField', index, 'medication', extraMed.medication);
                            }
                          "
                          @focus="onFocus"
                          @blur="onBlur" />
                      </div>
                      <div v-else>
                        <MedicationSearchAutoComplete
                          :disabled="!props.isEditable"
                          :data="medicationSearchBoxTerms"
                          :required="true"
                          :allow-free-text-input="true"
                          :menu-props="{
                            contentClass: 'medication-autocomplete-menu',
                            maxHeight: 300,
                            position: 'absolute',
                            transition: false,
                            closeOnClick: true,
                            closeOnContentClick: true,
                            eager: true,
                          }"
                          @item-selected="
                            (event) => {
                              handleSelectedExtraMedication(event, index);
                              onInput(event);
                              emit('updateExtraMedicationField', index, 'medication', extraMed.medication);
                            }
                          "
                          @focus="onFocus"
                          @blur="onBlur" />
                      </div>
                    </template>
                  </FeedbackInputWrapper>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <div v-if="extraMed && 'frequency' in extraMed">
                  <FeedbackInputWrapper
                    :field-id="'extraMedicationFrequency' + index"
                    :order-key="orderInfoPreview.orderKey"
                    :model-value="extraMed.frequency"
                    :label="'extraMedicationFrequency' + index">
                    <template #default="{ onInput, onChange, onFocus, onBlur }">
                      <v-text-field
                        v-model="extraMed.frequency"
                        type="text"
                        placeholder="Dosing Schedule"
                        label="Dosing Schedule"
                        class="lh--force-white-background"
                        :readonly="!props.isEditable"
                        :disabled="!props.isEditable"
                        @input="onInput"
                        @change="
                          (e) => {
                            onChange(e);
                            emit('updateExtraMedicationField', index, 'frequency', extraMed.frequency);
                          }
                        "
                        @focus="onFocus"
                        @blur="onBlur" />
                    </template>
                  </FeedbackInputWrapper>
                </div>
              </v-col>
              <v-col cols="3">
                <div v-if="extraMed && 'dailyDosage' in extraMed">
                  <FeedbackInputWrapper
                    :field-id="'extraMedicationDailyDosage' + index"
                    :order-key="orderInfoPreview.orderKey"
                    :model-value="extraMed.dailyDosage"
                    :label="'extraMedicationDailyDosage' + index">
                    <template #default="{ onInput, onChange, onFocus, onBlur }">
                      <v-text-field
                        v-model="extraMed.dailyDosage"
                        type="text"
                        placeholder="Dosage"
                        label="Dosage"
                        class="lh--force-white-background"
                        :readonly="!props.isEditable"
                        :disabled="!props.isEditable"
                        @input="onInput"
                        @change="
                          (e) => {
                            onChange(e);
                            emit('updateExtraMedicationField', index, 'dailyDosage', extraMed.dailyDosage);
                          }
                        "
                        @focus="onFocus"
                        @blur="onBlur" />
                    </template>
                  </FeedbackInputWrapper>
                </div>
              </v-col>
              <v-col cols="3">
                <div v-if="extraMed && 'nationalDrugCode' in extraMed">
                  <FeedbackInputWrapper
                    :field-id="'extraMedicationNationalDrugCode' + index"
                    :order-key="orderInfoPreview.orderKey"
                    :model-value="extraMed.nationalDrugCode"
                    :label="'extraMedicationDailyDosage' + index">
                    <template #default="{ onInput, onChange, onFocus, onBlur }">
                      <v-text-field
                        v-model="extraMed.nationalDrugCode"
                        type="text"
                        placeholder="National Drug Code (NDC)"
                        label="National Drug Code (NDC)"
                        class="lh--force-white-background"
                        :readonly="!props.isEditable"
                        :disabled="!props.isEditable"
                        @input="onInput"
                        @change="
                          (e) => {
                            onChange(e);
                            emit('updateExtraMedicationField', index, 'nationalDrugCode', extraMed.nationalDrugCode);
                          }
                        "
                        @focus="onFocus"
                        @blur="onBlur" />
                    </template>
                  </FeedbackInputWrapper>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <div v-if="extraMed && 'strength' in extraMed">
                  <FeedbackInputWrapper
                    :field-id="'extraMedicationStrength' + index"
                    :order-key="orderInfoPreview.orderKey"
                    :model-value="extraMed.strength"
                    :label="'extraMedicationStrength' + index">
                    <template #default="{ onInput, onChange, onFocus, onBlur }">
                      <v-text-field
                        v-model="extraMed.strength"
                        type="text"
                        placeholder="Strength"
                        label="Strength"
                        class="lh--force-white-background"
                        :readonly="!props.isEditable"
                        :disabled="!props.isEditable"
                        @input="onInput"
                        @change="
                          (e) => {
                            onChange(e);
                            emit('updateExtraMedicationField', index, 'strength', extraMed.strength);
                          }
                        "
                        @focus="onFocus"
                        @blur="onBlur" />
                    </template>
                  </FeedbackInputWrapper>
                </div>
              </v-col>
              <v-col cols="4">
                <div v-if="extraMed && 'routeAdministration' in extraMed">
                  <FeedbackInputWrapper
                    :field-id="'extraMedicationRouteAdministration' + index"
                    :order-key="orderInfoPreview.orderKey"
                    :model-value="extraMed.routeAdministration"
                    :error-messages="
                      props.isEditable &&
                      !extraMedFieldNotEmpty(extraMed, 'routeAdministration') &&
                      !isNaN(extraMed.routeAdministration) &&
                      props.formButtonClicked
                        ? 'Please enter value for route of administration.'
                        : null
                    "
                    :label="'extraMedicationRouteAdministration' + index">
                    <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
                      <v-text-field
                        v-model="extraMed.routeAdministration"
                        type="text"
                        placeholder="Route of Administration"
                        label="Route of Administration"
                        class="lh--force-white-background"
                        :readonly="!props.isEditable"
                        :disabled="!props.isEditable"
                        :error-messages="errorMessages"
                        @input="onInput"
                        @change="
                          (e) => {
                            onChange(e);
                            emit(
                              'updateExtraMedicationField',
                              index,
                              'routeAdministration',
                              extraMed.routeAdministration
                            );
                          }
                        "
                        @focus="onFocus"
                        @blur="onBlur" />
                    </template>
                  </FeedbackInputWrapper>
                </div>
              </v-col>
              <v-col cols="4">
                <div v-if="extraMed && 'quantityPer30Days' in extraMed">
                  <FeedbackInputWrapper
                    :field-id="'extraMedicationQuantityPer30Days' + index"
                    :order-key="orderInfoPreview.orderKey"
                    :model-value="extraMed.quantityPer30Days"
                    :error-messages="
                      props.isEditable &&
                      !extraMedFieldNotEmpty(extraMed, 'quantityPer30Days') &&
                      !isNaN(extraMed.quantityPer30Days) &&
                      props.formButtonClicked
                        ? 'Please enter a numerical value for quantity.'
                        : null
                    "
                    :label="'extraMedicationQuantityPer30Days' + index">
                    <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
                      <v-text-field
                        v-model="extraMed.quantityPer30Days"
                        type="text"
                        placeholder="Quantity"
                        label="Quantity*"
                        class="lh--force-white-background"
                        :readonly="!props.isEditable"
                        :disabled="!props.isEditable"
                        :error-messages="errorMessages"
                        @input="onInput"
                        @change="
                          (e) => {
                            onChange(e);
                            emit('updateExtraMedicationField', index, 'quantityPer30Days', extraMed.quantityPer30Days);
                          }
                        "
                        @focus="onFocus"
                        @blur="onBlur" />
                    </template>
                  </FeedbackInputWrapper>
                </div>
              </v-col>
              <v-col cols="4">
                <div v-if="extraMed && 'expectedLengthOfTherapy' in extraMed">
                  <FeedbackInputWrapper
                    :field-id="'extraMedicationExpectedLengthOfTherapy' + index"
                    :order-key="orderInfoPreview.orderKey"
                    :model-value="extraMed.expectedLengthOfTherapy"
                    :error-messages="
                      props.isEditable &&
                      !extraMedFieldNotEmpty(extraMed, 'expectedLengthOfTherapy') &&
                      !isNaN(extraMed.expectedLengthOfTherapy) &&
                      props.formButtonClicked
                        ? 'Please enter length of therapy in days.'
                        : null
                    "
                    :label="'extraMedicationExpectedLengthOfTherapy' + index">
                    <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
                      <v-text-field
                        v-model="extraMed.expectedLengthOfTherapy"
                        type="text"
                        placeholder="Duration of Therapy"
                        label="Duration of Therapy*"
                        class="lh--force-white-background"
                        :readonly="!props.isEditable"
                        :disabled="!props.isEditable"
                        :error-messages="errorMessages"
                        @input="onInput"
                        @change="
                          (e) => {
                            onChange(e);
                            emit(
                              'updateExtraMedicationField',
                              index,
                              'expectedLengthOfTherapy',
                              extraMed.expectedLengthOfTherapy
                            );
                          }
                        "
                        @focus="onFocus"
                        @blur="onBlur" />
                    </template>
                  </FeedbackInputWrapper>
                </div>
              </v-col>
              <v-col cols="4">
                <div v-if="extraMed && 'treatmentStartDate' in extraMed">
                  <FeedbackInputWrapper
                    :field-id="'extraMedicationTreatmentStartDate' + index"
                    :order-key="orderInfoPreview.orderKey"
                    :model-value="extraMed.treatmentStartDate"
                    :error-messages="
                      props.isEditable &&
                      !extraMedFieldNotEmpty(extraMed, 'treatmentStartDate') &&
                      !isNaN(extraMed.treatmentStartDate) &&
                      props.formButtonClicked
                        ? 'Please enter treatment start date.'
                        : null
                    "
                    :label="'extraMedicationTreatmentStartDate' + index">
                    <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
                      <v-text-field
                        v-model="extraMed.treatmentStartDate"
                        type="date"
                        placeholder="Treatment Start Date"
                        label="Treatment Start Date"
                        class="lh--force-white-background"
                        :readonly="!props.isEditable"
                        :disabled="!props.isEditable"
                        :error-messages="errorMessages"
                        @input="onInput"
                        @change="
                          (e) => {
                            onChange(e);
                            emit(
                              'updateExtraMedicationField',
                              index,
                              'treatmentStartDate',
                              extraMed.treatmentStartDate
                            );
                          }
                        "
                        @focus="onFocus"
                        @blur="onBlur" />
                    </template>
                  </FeedbackInputWrapper>
                </div>
              </v-col>
              <v-col cols="1" class="d-flex align-center justify-center">
                <v-btn
                  v-if="props.isEditable"
                  icon="mdi-delete"
                  variant="text"
                  color="error"
                  @click="emit('removeExtraMedication', index)" />
              </v-col>
            </v-row>
          </div>
        </v-col>
        <v-col v-if="isFax" cols="12">
          <v-btn
            v-if="props.isEditable"
            color="primary"
            variant="outlined"
            prepend-icon="mdi-plus"
            @click="emit('addExtraMedication')">
            Add Extra Medication
          </v-btn>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import { generateRandom4DigitNumber } from "@/utils";
import { getMedicationDefaultsForName, applyMedicationDefaultsToTarget } from "@/utils/clientConfig";
import MedicationSearchAutoComplete from "@/components/MedicationSearchAutoComplete.vue";
import FeedbackInputWrapper from "@/components/FeedbackInputWrapper.vue";
import { useRequirementsStore } from "@/stores";
const requirementsStore = useRequirementsStore();

onMounted(async () => {
  // Load client config for current user if not already loaded
  if (!requirementsStore.clientConfig) {
    await requirementsStore.refreshCurrentUserClientConfig();
  }
});

const selectedMedication = ref(null);
const newMedicationSelected = ref(false);

const emit = defineEmits([
  "updateOrderInfoField",
  "updateExtraMedicationField",
  "addExtraMedication",
  "removeExtraMedication",
  "removePrimaryMedication",
  "changeFormulary",
]);

const props = defineProps({
  orderInfo: {
    type: Object,
    required: true,
  },
  isEditable: {
    type: Boolean,
    default() {
      return false;
    },
  },
  formButtonClicked: {
    type: Boolean,
    default() {
      return false;
    },
  },
  medicationSearchBox: {
    type: Object,
    default() {
      return {};
    },
  },
});

const orderInfoPreview = ref(props.orderInfo);

const medicationSearchBoxTerms = computed(() => {
  return {
    label: "Medication",
    searchId: generateRandom4DigitNumber(),
  };
});

const isFax = computed(() => orderInfoPreview.value.submissionMethod === "eFax");
const isManual = computed(() => orderInfoPreview.value.submissionMethod === "Manual");
const isCoverMyMeds = computed(() => orderInfoPreview.value.submissionMethod === "CoverMyMeds");

function fieldNotEmpty(field) {
  const value = orderInfoPreview.value[field];
  return value !== null && value.trim() !== "";
}

function numericalFieldValid(field) {
  const value = orderInfoPreview.value[field];
  return value !== null && value.trim() !== "" && !isNaN(value);
}

function nationalDrugCodeValid(field) {
  const value = orderInfoPreview.value[field];
  return value !== null && value.trim() !== "" && value.length === 11 && !isNaN(value);
}

function packageSizeUnitValid(field) {
  const value = orderInfoPreview.value[field];
  return (
    value !== null && value.trim() !== "" && requirementsStore.clientConfig?.intake?.packageSizeUnits.includes(value)
  );
}

function extraMedFieldNotEmpty(extraMed, field) {
  if (!extraMed[field]) return false;
  return extraMed[field].trim() !== "";
}

function getMedicationDefaultsForSelection(selectedItem) {
  return getMedicationDefaultsForName(
    selectedItem?.value?.name ?? "",
    requirementsStore.clientConfig?.priorAuth?.medicationDefaults
  );
}

watch(selectedMedication, (newSelectedMedication) => {
  if (newSelectedMedication) {
    orderInfoPreview.value.medication = newSelectedMedication.value.medication
      ? newSelectedMedication.value.medication
      : newSelectedMedication.value.label;
    orderInfoPreview.value.strength = newSelectedMedication.value.strength ? newSelectedMedication.value.strength : "";
    orderInfoPreview.value.routeAdministration = newSelectedMedication.value.routeAdministration
      ? newSelectedMedication.value.routeAdministration
      : "";
    orderInfoPreview.value.packageSizeUnit = newSelectedMedication.value.packageSizeUnit
      ? newSelectedMedication.value.packageSizeUnit
      : "";
    orderInfoPreview.value.nationalDrugCode = newSelectedMedication.value.nationalDrugCode
      ? newSelectedMedication.value.nationalDrugCode
      : "";
    const defaults = getMedicationDefaultsForSelection(newSelectedMedication);
    if (defaults) {
      applyMedicationDefaultsToTarget(defaults, orderInfoPreview.value, (field, value) =>
        emit("updateOrderInfoField", field, value)
      );
    }
    newMedicationSelected.value = true;
  }
});

function handleSelectedExtraMedication(newSelectedMedication, index) {
  if (newSelectedMedication) {
    const extraMed = orderInfoPreview.value.extraMedications[index];
    extraMed.medication = newSelectedMedication.value.medication
      ? newSelectedMedication.value.medication
      : newSelectedMedication.value.label;
    extraMed.strength = newSelectedMedication.value.strength ? newSelectedMedication.value.strength : "";
    extraMed.routeAdministration = newSelectedMedication.value.routeAdministration
      ? newSelectedMedication.value.routeAdministration
      : "";
    extraMed.packageSizeUnit = newSelectedMedication.value.packageSizeUnit
      ? newSelectedMedication.value.packageSizeUnit
      : "";
    extraMed.nationalDrugCode = newSelectedMedication.value.nationalDrugCode
      ? newSelectedMedication.value.nationalDrugCode
      : "";
    const defaults = getMedicationDefaultsForSelection(newSelectedMedication);
    if (defaults) {
      applyMedicationDefaultsToTarget(defaults, extraMed, (field, value) =>
        emit("updateExtraMedicationField", index, field, value)
      );
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../styles/pages/_patient-information-form.scss";
.medication-autocomplete-menu {
  position: absolute !important;
  z-index: 1000;
  transform: none !important;
}
</style>
